import Cookies from 'js-cookie';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  FC,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';

export type Theme = 'light' | 'dark';
export type DarkMode = 'true' | 'false';

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface IThemeProvider {
  children: ReactNode;
}

export const ThemeProvider: FC<IThemeProvider> = ({ children }) => {
  let storedTheme: Theme | null;
  const darkMode = Cookies.get('darkMode-global') as DarkMode | undefined;

  if (darkMode) {
    storedTheme = darkMode === 'true' ? 'dark' : 'light';
  } else {
    storedTheme = null;
  }

  const systemTheme = getSystemTheme();
  const initialTheme = storedTheme || systemTheme || 'light';

  const [theme, setTheme] = useState<Theme>(initialTheme);

  const toggleTheme = useCallback(() => {
    setTheme((prevTheme) => {
      const newTheme: Theme = prevTheme === 'light' ? 'dark' : 'light';
      const newDarkMode: DarkMode = prevTheme === 'light' ? 'true' : 'false';

      Cookies.set('darkMode-global', newDarkMode, {
        secure: true,
        sameSite: 'None',
        domain: '.unigine.com',
      });

      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.body.classList.remove('theme-light', 'theme-dark');
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  const contextValue = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

const getSystemTheme = (): Theme => {
  const prefersDarkMode =
    window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDarkMode ? 'dark' : 'light';
};
